import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import type { CustomerCardFormValues } from '../utils/CustomerCardUtils';
import type { AddressData, AddressInfo, AddressType, AddressFormErrors } from './CustomerAddressTypes';
import { isAddressesEqual, extractCustomerAddressFields, type CustomerAddresses } from './CustomerAddressUtils';
import type { ContractFlowValidationEngine } from '../../hooks/useFormValidationWithContractFlow';

type CustomerAddressContextType = {
    addresses: AddressInfo[];
    setAddresses: React.Dispatch<React.SetStateAction<AddressInfo[]>>;
    handleEditAddress: (type: AddressType) => void;
    isLoading: boolean;
    handleCopyToBilling: () => void;
    handleDeleteAddress: (type: AddressType) => void;
    handleSwapAddresses: () => void;
    getAddressByType: (type: AddressType) => AddressInfo;
    handleAddressFieldChanged: (type: AddressType, field: string, value: string) => void;
    formValidations: Record<AddressType, { formErrors: AddressFormErrors }>;
    handleAddBillingAddress: () => void;
    handleCopyBuyerAddress: (buyerAddress: CustomerAddresses) => void;
    disabled?: boolean;
    hasAddressErrors: () => boolean;
    hasPrimaryAddressErrors: () => boolean;
};

const CustomerAddressContext = createContext<CustomerAddressContextType | undefined>(undefined);

type CustomerAddressProviderProps = {
    children: ReactNode;
    initialFormValues: CustomerCardFormValues;
    validator: ContractFlowValidationEngine;
    onAddressFieldChange?: (addressType: AddressType, field: string, value: string) => void;
    disabled?: boolean;
};

export const CustomerAddressProvider = ({
    children,
    initialFormValues,
    validator,
    onAddressFieldChange,
    disabled
}: CustomerAddressProviderProps) => {
    const errorState = validator.getFormErrorState({ channel: 'inline', refresh: false }) as any;
    const formValidations: Record<AddressType, { formErrors: AddressFormErrors }> = useMemo(() => {
        return {
            primary: {
                formErrors: {
                    streetAddress: errorState?.streetAddress,
                    streetAddress2: errorState?.streetAddress2,
                    city: errorState?.city,
                    zip: errorState?.zip,
                    county: errorState?.county
                }
            },
            billing: {
                formErrors: errorState?.billingAddress as AddressFormErrors
            }
        };
    }, [errorState]);

    const [addresses, setAddresses] = useState<AddressInfo[]>(() => {
        const addressList: AddressInfo[] = [
            {
                title: 'Primary',
                type: 'primary' as const,
                mode: 'view' as const,
                data: extractCustomerAddressFields({ ...initialFormValues, zip: initialFormValues.zip || '' })
            }
        ];

        if (initialFormValues.billingAddress) {
            addressList.push({
                title: 'Billing',
                type: 'billing' as const,
                mode: 'view' as const,
                data: extractCustomerAddressFields(initialFormValues.billingAddress as AddressData)
            });
        }

        return addressList;
    });
    const [isLoading, setIsLoading] = useState(false);

    // Sync addresses state when initialFormValues change (e.g., buyer address copy)
    useEffect(() => {
        setAddresses((prevAddresses) => {
            const [primaryAddress] = prevAddresses;
            const updatedAddresses: AddressInfo[] = [
                {
                    ...primaryAddress,
                    data: extractCustomerAddressFields({ ...initialFormValues, zip: initialFormValues.zip || '' })
                }
            ];

            if (initialFormValues.billingAddress) {
                const existingBilling = prevAddresses.find((addr) => addr.type === 'billing');
                updatedAddresses.push({
                    title: 'Billing',
                    type: 'billing' as const,
                    mode: existingBilling?.mode ?? 'view',
                    data: extractCustomerAddressFields(initialFormValues.billingAddress as AddressData)
                });
            } else if (prevAddresses.find((addr) => addr.type === 'billing')) {
                const existingBilling = prevAddresses.find((addr) => addr.type === 'billing');
                if (existingBilling) {
                    updatedAddresses.push(existingBilling);
                }
            }

            return updatedAddresses;
        });
    }, [initialFormValues]);

    const hasPrimaryAddressFieldsErrors = useMemo(() => {
        return Object.values(formValidations.primary.formErrors).some((error) => error?.hasError);
    }, [formValidations.primary.formErrors]);

    useEffect(() => {
        if (hasPrimaryAddressFieldsErrors) {
            setAddresses((prevAddress) =>
                prevAddress.map((addr) => (addr.type === 'primary' ? { ...addr, mode: 'edit' as const } : addr))
            );
        }
    }, [hasPrimaryAddressFieldsErrors]);

    const handleAddBillingAddress = () => {
        const emptyBillingAddress: AddressData = {
            streetAddress: '',
            streetAddress2: '',
            city: '',
            state: '',
            zip: '',
            county: ''
        };

        setAddresses((prevAddresses) => [
            ...prevAddresses,
            {
                title: 'Billing',
                type: 'billing',
                mode: 'edit',
                data: extractCustomerAddressFields(emptyBillingAddress)
            }
        ]);

        validator.setValues({ billingAddress: emptyBillingAddress }, { mode: 'merge', validate: true });
    };

    const handleEditAddress = (type: AddressType) => {
        setAddresses((prevAddress) => prevAddress.map((addr) => (addr.type === type ? { ...addr, mode: 'edit' as const } : addr)));
    };

    const handleAddressFieldChanged = (type: AddressType, field: string, value: string) => {
        const trimmedValue = value.trim();
        setAddresses((prevAddresses) =>
            prevAddresses.map((addr) => {
                if (addr.type === type) {
                    return { ...addr, data: { ...addr.data, [field]: trimmedValue } };
                }
                return addr;
            })
        );

        if (onAddressFieldChange) {
            onAddressFieldChange(type, field, trimmedValue);
        }
    };

    const handleCopyToBilling = () => {
        const primaryAddress = addresses.find((addr) => addr.type === 'primary') as AddressInfo;

        const updatedBillingAddress: AddressData = extractCustomerAddressFields(primaryAddress.data);

        setAddresses((prevAddresses) => {
            const billingIndex = prevAddresses.findIndex((addr) => addr.type === 'billing');

            if (billingIndex >= 0) {
                return prevAddresses.map((addr) =>
                    addr.type === 'billing' ? { ...addr, data: { ...primaryAddress.data } } : addr
                );
            } else {
                return [...prevAddresses, { title: 'Billing', type: 'billing', mode: 'view', data: { ...primaryAddress.data } }];
            }
        });

        validator.setValues({ billingAddress: updatedBillingAddress }, { mode: 'merge', validate: true });
    };

    const handleDeleteAddress = (type: AddressType) => {
        if (type === 'billing') {
            // Error reset is handled by useEffect when initialFormValues.billingAddress changes
            validator.setValue('billingAddress', undefined, { validate: true });
        }
        setAddresses((prevAddresses) => prevAddresses.filter((addr) => addr.type !== type));
    };

    const handleSwapAddresses = () => {
        const primaryAddress = addresses.find((addr) => addr.type === 'primary');
        const billingAddress = addresses.find((addr) => addr.type === 'billing');
        if (!primaryAddress || !billingAddress) return;

        const updatedPrimaryAddress: AddressData = extractCustomerAddressFields(billingAddress.data);
        const updatedBillingAddress: AddressData = extractCustomerAddressFields(primaryAddress.data);

        setAddresses((prevAddresses) =>
            prevAddresses.map((addr) => {
                if (addr.type === 'primary') {
                    return { ...addr, data: { ...billingAddress.data } };
                } else {
                    return { ...addr, data: { ...primaryAddress.data } };
                }
            })
        );

        validator.setValues({ ...updatedPrimaryAddress, billingAddress: updatedBillingAddress }, { mode: 'merge', validate: true });
    };

    const getAddressByType = (type: AddressType) => {
        return addresses.find((addr) => addr.type === type) as AddressInfo;
    };

    const handleCopyBuyerAddress = (buyerAddress: CustomerAddresses) => {
        if (isAddressesEqual(addresses, buyerAddress)) {
            const updatedPrimaryAddress: AddressData = { ...buyerAddress.primaryAddress };
            const updatedBillingAddress: AddressData | undefined = buyerAddress.billingAddress
                ? { ...buyerAddress.billingAddress }
                : undefined;

            setIsLoading(true);
            setAddresses(() => {
                const updatedAddresses: AddressInfo[] = [
                    {
                        title: 'Primary',
                        type: 'primary' as const,
                        mode: 'view' as const,
                        data: updatedPrimaryAddress
                    }
                ];
                if (updatedBillingAddress) {
                    updatedAddresses.push({
                        title: 'Billing',
                        type: 'billing' as const,
                        mode: 'view' as const,
                        data: updatedBillingAddress
                    });
                }
                return updatedAddresses;
            });

            validator.setValues(
                {
                    ...updatedPrimaryAddress,
                    billingAddress: updatedBillingAddress
                },
                { mode: 'merge', validate: true }
            );
        }

        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    };

    const hasAddressErrors = (): boolean => {
        return Object.values(formValidations).some((validation) =>
            Object.values(validation.formErrors).some((error) => error.hasError)
        );
    };

    const hasPrimaryAddressErrors = (): boolean => {
        return hasPrimaryAddressFieldsErrors;
    };

    const contextValue: CustomerAddressContextType = {
        addresses,
        isLoading,
        setAddresses,
        handleEditAddress,
        handleCopyToBilling,
        handleDeleteAddress,
        handleSwapAddresses,
        getAddressByType,
        handleAddressFieldChanged,
        formValidations,
        handleAddBillingAddress,
        handleCopyBuyerAddress,
        disabled,
        hasAddressErrors,
        hasPrimaryAddressErrors
    };

    return <CustomerAddressContext.Provider value={contextValue}>{children}</CustomerAddressContext.Provider>;
};

export const useCustomerAddress = (): CustomerAddressContextType => {
    const context = useContext(CustomerAddressContext);
    if (context === undefined) {
        throw new Error('useCustomerAddress must be used within a CustomerAddressProvider');
    }
    return context;
};
